// src/pages/FicheFrais/components/ExpenseMetaCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "../ui/SectionCard";
import Button from "../../../components/Button/Button.jsx";
import TravelFields from "./TravelFields";
import CarpoolFields from "./CarpoolFields";
import MileageRouteCalculator from "./MileageRouteCalculator";

import { FaMoneyCheckAlt, FaRoad, FaSearch, FaSyncAlt } from "react-icons/fa";
import {
  getBudgetCodes,
  getBudgetRubriques,
} from "../../../services/expenseServices";

/* helpers */
const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const isActive = (row) =>
  row?.is_active === true || row?.is_active === 1 || row?.is_active === "1";

const toIdString = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

function getCodeRubriqueId(row) {
  return (
    row?.rubrique_id ||
    row?.rubrique?.id ||
    row?.Rubrique?.id ||
    row?.budget_rubrique_id ||
    ""
  );
}

function getCodeSubRubriqueId(row) {
  return (
    row?.sub_rubrique_id ||
    row?.sub_rubrique?.id ||
    row?.SubRubrique?.id ||
    row?.budget_sub_rubrique_id ||
    ""
  );
}

function getCodeRubriqueCode(row) {
  return (
    row?.rubrique?.code ||
    row?.Rubrique?.code ||
    row?.rubrique_code ||
    String(row?.code || "").match(/^[A-Z]+/)?.[0] ||
    ""
  );
}

function getCodeSubRubriqueLabel(row) {
  return (
    row?.sub_rubrique?.libelle ||
    row?.SubRubrique?.libelle ||
    row?.sub_rubrique_libelle ||
    ""
  );
}

function toLabel(row) {
  const code = row?.code || row?.code_budgetaire || row?.budget_code || "";
  const lib = row?.libelle || row?.label || row?.name || "";
  return code && lib ? `${code} — ${lib}` : code || lib || `Code #${row?.id}`;
}

function toLongSearchLabel(row) {
  return [
    row?.old_code,
    row?.code,
    row?.libelle,
    row?.rubrique?.code,
    row?.rubrique?.libelle,
    row?.sub_rubrique?.code,
    row?.sub_rubrique?.libelle,
    getCodeSubRubriqueLabel(row),
  ]
    .filter(Boolean)
    .join(" ");
}

function sortByOrdreThenLabel(a, b) {
  const ordreA = Number(a?.ordre ?? 9999);
  const ordreB = Number(b?.ordre ?? 9999);

  if (ordreA !== ordreB) return ordreA - ordreB;

  const labelA = `${a?.code || ""} ${a?.libelle || ""}`;
  const labelB = `${b?.code || ""} ${b?.libelle || ""}`;

  return labelA.localeCompare(labelB, "fr");
}

function sortCodes(a, b) {
  const rubriqueOrdreA = Number(a?.rubrique?.ordre ?? 9999);
  const rubriqueOrdreB = Number(b?.rubrique?.ordre ?? 9999);

  if (rubriqueOrdreA !== rubriqueOrdreB) {
    return rubriqueOrdreA - rubriqueOrdreB;
  }

  const subOrdreA = Number(a?.sub_rubrique?.ordre ?? 9999);
  const subOrdreB = Number(b?.sub_rubrique?.ordre ?? 9999);

  if (subOrdreA !== subOrdreB) {
    return subOrdreA - subOrdreB;
  }

  const ordreA = Number(a?.ordre ?? 9999);
  const ordreB = Number(b?.ordre ?? 9999);

  if (ordreA !== ordreB) return ordreA - ordreB;

  return String(a?.code || "").localeCompare(String(b?.code || ""), "fr");
}

export default function ExpenseMetaCard({
  eventSelected,
  draft,
  setDraft,
  onComputeDistance,
  computingDistance = false,
  setComputingDistance,
  residenceAddress = "",
}) {
  const [codes, setCodes] = useState([]);
  const [rubriques, setRubriques] = useState([]);

  const [loadingCodes, setLoadingCodes] = useState(true);
  const [errorCodes, setErrorCodes] = useState("");

  const [selectedRubriqueId, setSelectedRubriqueId] = useState("");
  const [selectedSubRubriqueId, setSelectedSubRubriqueId] = useState("");
  const [q, setQ] = useState("");

  const loadCodes = async () => {
    try {
      setErrorCodes("");
      setLoadingCodes(true);

      const [rubriquesData, codesData] = await Promise.all([
        getBudgetRubriques({ activeOnly: true }),
        getBudgetCodes({ activeOnly: true }),
      ]);

      setRubriques(Array.isArray(rubriquesData) ? rubriquesData : []);
      setCodes(Array.isArray(codesData) ? codesData : []);
    } catch (e) {
      console.error(e);
      setErrorCodes("Impossible de charger les codes budgétaires.");
      setRubriques([]);
      setCodes([]);
    } finally {
      setLoadingCodes(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const activeCodes = useMemo(() => {
    return (codes || []).filter(isActive).sort(sortCodes);
  }, [codes]);

  const rubriqueOptions = useMemo(() => {
    return (rubriques || []).filter(isActive).sort(sortByOrdreThenLabel);
  }, [rubriques]);

  const selectedRubrique = useMemo(() => {
    if (!selectedRubriqueId) return null;

    return rubriqueOptions.find(
      (rubrique) => String(rubrique.id) === String(selectedRubriqueId),
    );
  }, [rubriqueOptions, selectedRubriqueId]);

  const subRubriqueOptions = useMemo(() => {
    return (selectedRubrique?.sub_rubriques || [])
      .filter(isActive)
      .sort(sortByOrdreThenLabel);
  }, [selectedRubrique]);

  const selectedCode = useMemo(() => {
    if (!draft?.code_budgetaire_id) return null;

    return activeCodes.find(
      (code) => String(code.id) === String(draft.code_budgetaire_id),
    );
  }, [activeCodes, draft?.code_budgetaire_id]);

  useEffect(() => {
    if (!selectedCode) return;

    const rubriqueId = getCodeRubriqueId(selectedCode);
    const subRubriqueId = getCodeSubRubriqueId(selectedCode);

    if (rubriqueId) {
      setSelectedRubriqueId(String(rubriqueId));
    }

    if (subRubriqueId) {
      setSelectedSubRubriqueId(String(subRubriqueId));
    }
  }, [selectedCode]);

  const filteredCodes = useMemo(() => {
    const needle = normalize(q);
    const selectedRubriqueCode = selectedRubrique?.code || "";

    return activeCodes.filter((row) => {
      const rowRubriqueId = getCodeRubriqueId(row);
      const rowRubriqueCode = getCodeRubriqueCode(row);
      const rowSubRubriqueId = getCodeSubRubriqueId(row);

      if (selectedRubriqueId) {
        const sameRubrique =
          String(rowRubriqueId) === String(selectedRubriqueId) ||
          String(rowRubriqueCode) === String(selectedRubriqueCode);

        if (!sameRubrique) return false;
      }

      if (selectedSubRubriqueId) {
        if (String(rowSubRubriqueId) !== String(selectedSubRubriqueId)) {
          return false;
        }
      }

      if (needle && !normalize(toLongSearchLabel(row)).includes(needle)) {
        return false;
      }

      return true;
    });
  }, [
    activeCodes,
    q,
    selectedRubrique,
    selectedRubriqueId,
    selectedSubRubriqueId,
  ]);

  const disabledBecauseNoEvent = !eventSelected;

  const isDetailedMileage =
    draft?.trajet_necessaire &&
    draft?.moyen_transport === "voiture" &&
    draft?.mileage_mode === "detailed" &&
    (!draft?.covoiturage || draft?.covoiturage_role === "Conducteur");

  // ✅ Mode UI dérivé des champs DB
  const mode = !draft?.trajet_necessaire
    ? "none"
    : draft?.covoiturage
      ? "carpool"
      : "travel";

  const resetBudgetCode = () => {
    setDraft((p) => ({
      ...p,
      code_budgetaire_id: null,
    }));
  };

  const handleRubriqueChange = (value) => {
    setSelectedRubriqueId(value);
    setSelectedSubRubriqueId("");
    setQ("");
    resetBudgetCode();
  };

  const handleSubRubriqueChange = (value) => {
    setSelectedSubRubriqueId(value);
    setQ("");
    resetBudgetCode();
  };

  const handleCodeChange = (value) => {
    const nextId = value ? Number(value) : null;
    const code = activeCodes.find((row) => String(row.id) === String(value));

    if (code) {
      const rubriqueId = getCodeRubriqueId(code);
      const subRubriqueId = getCodeSubRubriqueId(code);

      if (rubriqueId) {
        setSelectedRubriqueId(String(rubriqueId));
      }

      if (subRubriqueId) {
        setSelectedSubRubriqueId(String(subRubriqueId));
      }
    }

    setDraft((p) => ({
      ...p,
      code_budgetaire_id: nextId,
    }));
  };

  const setMode = (nextMode) => {
    setDraft((p) => {
      if (nextMode === "none") {
        return {
          ...p,
          trajet_necessaire: false,
          moyen_transport: null,
          aller_retour: true,
          depart: "",
          arrivee: "",
          distance_km: "",
          mileage_result: null,

          covoiturage: false,
          covoiturage_role: "",
          covoiturage_nb_passagers: 0,
          covoiturage_avec: "",
        };
      }

      if (nextMode === "travel") {
        return {
          ...p,
          trajet_necessaire: true,
          moyen_transport: p.moyen_transport || "voiture",
          covoiturage: false,
          covoiturage_role: "",
          covoiturage_nb_passagers: 0,
          covoiturage_avec: "",
          arrivee: p.arrivee || eventSelected?.lieu_event || p.arrivee,
          mileage_result: null,
        };
      }

      return {
        ...p,
        trajet_necessaire: true,
        moyen_transport: "voiture",
        covoiturage: true,
        arrivee: p.arrivee || eventSelected?.lieu_event || p.arrivee,
        mileage_result: null,
      };
    });
  };

  const setMoyenTransport = (val) => {
    setDraft((p) => {
      const next = {
        ...p,
        trajet_necessaire: true,
        moyen_transport: val || null,
        mileage_result: null,
      };

      if (val !== "voiture") {
        next.covoiturage = false;
        next.covoiturage_role = "";
        next.covoiturage_nb_passagers = 0;
        next.covoiturage_avec = "";
      }

      return next;
    });
  };

  return (
    <SectionCard
      id="meta"
      title="Informations de la fiche"
      subtitle="Choisissez la rubrique, la sous-rubrique, le code budgétaire et indiquez votre mode de déplacement."
      icon={<FaMoneyCheckAlt />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={loadingCodes ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={loadCodes}
            icon={FaSyncAlt}
          />
        </div>
      }
    >
      {disabledBecauseNoEvent ? (
        <div className="rounded-xl bg-amber-50 p-4 text-amber-900 ring-1 ring-amber-200">
          Sélectionnez un événement du calendrier ou renseignez manuellement le
          nom, la date et le lieu pour continuer.
        </div>
      ) : null}

      {/* ====== Code budgétaire ====== */}
      <div
        className={[
          "mt-4 rounded-2xl bg-white p-4 ring-1 ring-slate-200",
          disabledBecauseNoEvent ? "pointer-events-none opacity-60" : "",
        ].join(" ")}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              <FaMoneyCheckAlt className="text-slate-500" />
              Code budgétaire *
            </div>
            <div className="mt-1 text-sm text-slate-500">
              Sélectionnez dans l’ordre : rubrique, sous-rubrique, puis code.
            </div>
          </div>

          <div className="relative w-full md:w-[320px]">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un code ou un libellé…"
              className="h-10 w-full rounded-xl bg-white px-3 pl-9 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-800"
            />
          </div>
        </div>

        {errorCodes ? (
          <div className="mt-3 rounded-xl bg-rose-50 p-3 text-rose-800 ring-1 ring-rose-200">
            {errorCodes}
          </div>
        ) : null}

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <label className="block">
            <div className="mb-1 text-sm font-semibold text-slate-700">
              Rubrique *
            </div>
            <select
              value={selectedRubriqueId}
              onChange={(e) => handleRubriqueChange(e.target.value)}
              disabled={loadingCodes}
              className="w-full rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-800"
            >
              <option value="">
                {loadingCodes ? "Chargement..." : "— Choisir une rubrique —"}
              </option>
              {rubriqueOptions.map((rubrique) => (
                <option key={rubrique.id} value={rubrique.id}>
                  {rubrique.code} — {rubrique.libelle}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <div className="mb-1 text-sm font-semibold text-slate-700">
              Sous-rubrique
            </div>
            <select
              value={selectedSubRubriqueId}
              onChange={(e) => handleSubRubriqueChange(e.target.value)}
              disabled={loadingCodes || !selectedRubriqueId}
              className="w-full rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-800"
            >
              <option value="">
                {!selectedRubriqueId
                  ? "— Choisir une rubrique d’abord —"
                  : "— Toutes les sous-rubriques —"}
              </option>
              {subRubriqueOptions.map((subRubrique) => (
                <option key={subRubrique.id} value={subRubrique.id}>
                  {subRubrique.code
                    ? `${subRubrique.code} — ${subRubrique.libelle}`
                    : subRubrique.libelle}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <div className="mb-1 text-sm font-semibold text-slate-700">
              Code *
            </div>
            <select
              value={toIdString(draft?.code_budgetaire_id)}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={loadingCodes || !selectedRubriqueId}
              className="w-full rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-800"
            >
              <option value="">
                {loadingCodes
                  ? "Chargement..."
                  : !selectedRubriqueId
                    ? "— Choisir une rubrique d’abord —"
                    : "— Sélectionner un code —"}
              </option>

              {filteredCodes.map((r) => (
                <option key={r.id} value={r.id}>
                  {toLabel(r)}
                  {getCodeSubRubriqueLabel(r)
                    ? ` · ${getCodeSubRubriqueLabel(r)}`
                    : ""}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600 ring-1 ring-slate-200">
          <div className="flex flex-wrap items-center gap-2">
            <span>
              Réunion ou événement :{" "}
              <strong>{eventSelected?.titre || "—"}</strong>
            </span>

            {eventSelected?.source === "manual" ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
                Saisie manuelle
              </span>
            ) : null}
          </div>

          {eventSelected ? (
            <div className="mt-1 text-xs text-slate-500">
              {eventSelected.start || "—"} • {eventSelected.lieu_event || "—"}
            </div>
          ) : null}

          <div className="mt-1">
            Code choisi :{" "}
            <strong>
              {selectedCode ? toLabel(selectedCode) : "Aucun code sélectionné"}
            </strong>
          </div>

          {selectedCode?.old_code ? (
            <div className="mt-1 text-xs text-slate-500">
              Ancien code : {selectedCode.old_code}
            </div>
          ) : null}

          {!loadingCodes && selectedRubriqueId && !filteredCodes.length ? (
            <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
              Aucun code ne correspond à cette rubrique, cette sous-rubrique ou
              cette recherche.
            </div>
          ) : null}
        </div>
      </div>

      {/* ====== Déplacement ====== */}
      <div
        className={[
          "mt-4 rounded-2xl bg-white p-4 ring-1 ring-slate-200",
          disabledBecauseNoEvent ? "pointer-events-none opacity-60" : "",
        ].join(" ")}
      >
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <FaRoad className="text-slate-500" />
          Déplacement
        </div>

        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <label className="block">
            <div className="mb-1 text-sm font-semibold text-slate-700">
              Mode *
            </div>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-800"
            >
              <option value="none">Aucun</option>
              <option value="travel">Trajet</option>
              <option value="carpool">Covoiturage</option>
            </select>
          </label>

          <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Résumé
            </div>
            <div className="mt-1 text-sm text-slate-700">
              {mode === "none"
                ? "Aucun déplacement déclaré."
                : mode === "travel"
                  ? "Trajet déclaré (champs trajet à compléter)."
                  : "Covoiturage déclaré (trajet + infos covoiturage)."}
            </div>
          </div>
        </div>

        {mode !== "none" ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block">
              <div className="mb-1 text-sm font-semibold text-slate-700">
                Moyen de transport *
              </div>
              <select
                value={draft?.moyen_transport || ""}
                onChange={(e) => setMoyenTransport(e.target.value)}
                className="w-full rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-800"
              >
                <option value="">— Sélectionner —</option>
                <option value="voiture">Voiture</option>
                <option value="train">Train</option>
                <option value="transport">Transport</option>
                <option value="autre">Autre</option>
              </select>
              <div className="mt-1 text-xs text-slate-500">
                Le covoiturage est disponible uniquement si “Voiture”.
              </div>
            </label>
          </div>
        ) : null}

        {mode !== "none" ? (
          <div className="mt-4">
            {isDetailedMileage ? (
              <MileageRouteCalculator
                draft={draft}
                setDraft={setDraft}
                residenceAddress={residenceAddress}
                computingDistance={computingDistance}
                setComputingDistance={setComputingDistance}
              />
            ) : (
              <TravelFields
                draft={draft}
                setDraft={setDraft}
                eventLieu={eventSelected?.lieu_event || ""}
                onComputeDistance={onComputeDistance}
                computingDistance={computingDistance}
                residenceAddress={residenceAddress}
              />
            )}
          </div>
        ) : null}

        {mode === "carpool" ? (
          <div className="mt-4">
            <CarpoolFields
              draft={draft}
              setDraft={setDraft}
              detailedMileage={isDetailedMileage}
            />
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
